import * as shared from '@aloft/toolkit-shared';
import { expected } from '@aloft/toolkit-shared';
import { Flex } from '@aloft/toolkit-sui';

import { useLayoutEffect } from 'react';
import { Outlet, useLocation, useParams } from 'react-router';

import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary.tsx';
import { ToolkitGenericFallback } from '../../components/ToolkitGenericFallback.tsx';
import { useAlertToastHoverEmphasis } from '../../hooks/useAlertToastHoverEmphasis.ts';
import { useAlertToastHoverSync } from '../../hooks/useAlertToastHoverSync.ts';
import { useSyncTriggeredAlertsToCurrentRoute } from '../../hooks/useSyncTriggeredAlertsToCurrentRoute.ts';
import { useTriggeredAlertToasts } from '../../hooks/useTriggeredAlertToasts.ts';
import { MainLayout } from '../../layout/MainLayout.tsx';
import { logger } from '../../logging/logging.ts';
import { AlertsProvider } from '../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { Tkv2ReferenceViewDrawer } from '../../modules/referenceView/Tkv2ReferenceViewDrawer.tsx';
import {
  clearAlertsPanelAutoOpenedForNavBlock,
  setReferenceViewOpen,
} from '../../redux/features/referenceBarUISlice.ts';
import { selectReferenceBarUI } from '../../redux/features/selectors/referenceBarUISelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { Tkv2ControllerReferenceBarContext } from '../Tkv2ControllerReferenceBarContext.ts';
import { Tkv2OrderContext } from '../Tkv2OrderContext.ts';
import { useNewTkv2OrderContext } from '../hooks/useNewTkv2OrderContext.ts';
import { useNewTkv2ReferenceBarUserInterface } from '../hooks/useNewTkv2ReferenceBarUserInterface.ts';
import { useTkv2OrderContext } from '../hooks/useTkv2ControllerOrder.ts';

const log = logger();

export const Tkv2OrderRootRouteElement = () => {
  const { orderId } = useParams();
  const ctx = useNewTkv2OrderContext(expected(orderId));
  const referenceBarUI = useNewTkv2ReferenceBarUserInterface();

  if (!ctx) return null;

  (window as any).ctx = ctx;
  (window as any).shared = shared;

  return (
    <Tkv2OrderContext.Provider value={ctx}>
      <Tkv2ControllerReferenceBarContext.Provider value={referenceBarUI}>
        <AlertsProvider>
          <Root />
        </AlertsProvider>
      </Tkv2ControllerReferenceBarContext.Provider>
    </Tkv2OrderContext.Provider>
  );
};

const Root = () => {
  const dispatch = useAppDispatch();
  const ctx = useTkv2OrderContext();
  const { subject } = ctx;
  useTriggeredAlertToasts();
  useAlertToastHoverSync();
  useAlertToastHoverEmphasis();
  useSyncTriggeredAlertsToCurrentRoute();

  const { pathname } = useLocation();
  const { alertsPanelAutoOpenedForNavBlock } = useAppSelector(selectReferenceBarUI);
  useLayoutEffect(() => {
    if (alertsPanelAutoOpenedForNavBlock) {
      dispatch(clearAlertsPanelAutoOpenedForNavBlock());
      return;
    }
    dispatch(setReferenceViewOpen(false));
  }, [alertsPanelAutoOpenedForNavBlock, dispatch, pathname]);

  return (
    <MainLayout
      title={
        shared.orUndefined(shared.addressToString)(subject?.body.getStructuredData('address')) ?? ''
      }
      showNav={false}
    >
      <Flex height="100%" width="100%">
        <ErrorBoundary
          Fallback={ToolkitGenericFallback}
          onError={() => log.fatal(ctx, 'ToolkitControllerOrderGlobal ctx dump')}
        >
          <Tkv2ReferenceViewDrawer>
            <Outlet />
          </Tkv2ReferenceViewDrawer>
        </ErrorBoundary>
      </Flex>
    </MainLayout>
  );
};
